import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import EventsDesk, {
    DeskRibbon,
    DeskPanel,
    DeskTitle,
    DeskNote,
    DeskEmpty,
    DESK_BUTTON_CLASS,
    DESK_PRIMARY_CLASS,
    DESK_PRIMARY_STYLE
} from '../../../components/staff/events/EventsDesk';
import EventWizard from '../../../components/staff/events/EventWizard';
import {
    EVENT_TYPES,
    WEEKDAY_INITIAL,
    WEEKDAY_SHORT,
    addDaysToKey,
    canManageEvent,
    dayKey,
    eventTimeLabel,
    eventsByDay,
    isValidZone,
    localMinutes,
    offsetLabel,
    pad,
    rangeForView,
    readStoredZone,
    readableMonth,
    readableRange,
    shiftMonthKey,
    timeLabel,
    typeColor,
    weekKeys,
    weekStartKey,
    writeStoredZone,
    zonedToUtc
} from '../../../components/staff/events/eventsShared';
import { listTimeZones } from '../../../utils/calendarTime';
import { deleteStaffEvent, getStaffEventPeople, getStaffEvents } from '../../../services/api';

const VIEWS = [
    { key: 'month', label: 'Month' },
    { key: 'week', label: 'Week' },
    { key: 'day', label: 'Day' }
];

const DEFAULT_HOUR_WINDOW = { first: 12, last: 23 };

const readableDayLabel = (key) => {
    const [year, month, day] = key.split('-').map(Number);

    return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString(undefined, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        timeZone: 'UTC'
    });
};

const hourLabel = (hour) => `${pad(hour)}:00`;

function EventChip({ event, zone, onClick }) {
    return (
        <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onClick(event); }}
            className="flex w-full items-center gap-1 overflow-hidden rounded-sm px-[4px] py-[2px] text-left text-[11px] text-white transition-opacity hover:opacity-85"
            style={{ backgroundColor: typeColor(event.type) }}
            title={`${event.title} - ${event.host} - ${eventTimeLabel(event, zone)}`}
        >
            <span className="shrink-0 font-['Jersey_10'] text-[12px] leading-none tracking-[1px]">
                {timeLabel(event.startTime, zone)}
            </span>
            <span className="truncate">{event.title}</span>
        </button>
    );
}

function MonthGrid({ cells, byDay, todayKey, selected, monthKey, zone, onSelect, onOpen }) {
    return (
        <div className="grid grid-cols-7 gap-[2px]">
            {WEEKDAY_INITIAL.map((day, index) => (
                <span
                    key={index}
                    className="py-1 text-center font-['Jersey_10'] text-[13px] uppercase tracking-[1px] text-gray-500"
                >
                    {day}
                </span>
            ))}

            {cells.map(key => {
                const entries = byDay.get(key) || [];
                const isSelected = key === selected;
                const inMonth = key.slice(0, 7) === monthKey.slice(0, 7);
                const day = Number(key.slice(8));

                return (
                    <div
                        key={key}
                        role="button"
                        tabIndex={0}
                        onClick={() => onSelect(key)}
                        onKeyDown={(e) => { if (e.key === 'Enter') onSelect(key); }}
                        className={`flex min-h-[86px] cursor-pointer flex-col gap-[2px] border p-1 transition-colors ${
                            isSelected
                                ? 'border-maroon bg-white'
                                : inMonth
                                    ? 'border-gray-200 bg-white hover:bg-gray-50'
                                    : 'border-gray-100 bg-gray-50 hover:bg-gray-100'
                        }`}
                    >
                        <div className="flex items-baseline justify-between">
                            <span
                                className={`font-['Jersey_10'] text-[15px] leading-none ${
                                    key === todayKey
                                        ? 'rounded bg-maroon px-[5px] py-[2px] text-white'
                                        : inMonth ? 'text-gray-800' : 'text-gray-400'
                                }`}
                            >
                                {day}
                            </span>
                            {entries.length > 0 && (
                                <span className="text-[10px] text-gray-400">{entries.length}</span>
                            )}
                        </div>

                        {entries.slice(0, 3).map(event => (
                            <EventChip key={event.id} event={event} zone={zone} onClick={onOpen} />
                        ))}

                        {entries.length > 3 && (
                            <span className="px-[4px] text-[10px] text-gray-500">+{entries.length - 3} more</span>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function WeekGrid({ days, byDay, todayKey, selected, zone, allHours, onToggleHours, onSelect, onOpen, onNewAt }) {
    const window = useMemo(() => {
        if (allHours) return { first: 0, last: 23 };

        let first = 24;
        let last = -1;

        for (const key of days) {
            for (const event of byDay.get(key) || []) {
                const startHour = Math.floor(localMinutes(event.startTime, zone) / 60);
                const endMinutes = localMinutes(event.endTime, zone);
                const endHour = Math.min(23, Math.floor((endMinutes === 0 ? 1440 : endMinutes) / 60));

                first = Math.min(first, startHour);
                last = Math.max(last, endHour);
            }
        }

        if (last < first) return DEFAULT_HOUR_WINDOW;

        return { first: Math.max(0, first - 1), last: Math.min(23, last + 1) };
    }, [days, byDay, zone, allHours]);

    const hours = useMemo(
        () => Array.from({ length: window.last - window.first + 1 }, (_, index) => window.first + index),
        [window]
    );

    const startingIn = useCallback(
        (key, hour) => (byDay.get(key) || []).filter(
            event => Math.floor(localMinutes(event.startTime, zone) / 60) === hour
        ),
        [byDay, zone]
    );

    return (
        <div className="flex flex-col gap-2">
            <div className="overflow-x-auto">
                <div className="min-w-[640px]">
                    <div className="grid grid-cols-[52px_repeat(7,1fr)] gap-[2px]">
                        <span></span>
                        {days.map(key => (
                            <button
                                key={key}
                                type="button"
                                onClick={() => onSelect(key)}
                                className={`flex flex-col items-center rounded-t px-1 py-1 transition-colors ${
                                    key === selected ? 'bg-maroon text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                <span className="font-['Jersey_10'] text-[13px] uppercase tracking-[1px]">
                                    {WEEKDAY_SHORT[(new Date(`${key}T00:00:00Z`).getUTCDay() + 6) % 7]}
                                </span>
                                <span
                                    className={`font-['Jersey_10'] text-[17px] leading-none ${
                                        key === todayKey && key !== selected ? 'text-maroon' : ''
                                    }`}
                                >
                                    {Number(key.slice(8))}
                                </span>
                            </button>
                        ))}
                    </div>

                    {hours.map(hour => (
                        <div key={hour} className="grid grid-cols-[52px_repeat(7,1fr)] gap-[2px] border-t border-gray-100">
                            <span className="py-[6px] pr-1 text-right font-['Jersey_10'] text-[13px] leading-none text-gray-400">
                                {hourLabel(hour)}
                            </span>

                            {days.map(key => {
                                const entries = startingIn(key, hour);

                                return (
                                    <div
                                        key={key}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => onSelect(key)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') onSelect(key); }}
                                        className={`group flex min-h-[30px] cursor-pointer flex-col gap-[2px] p-[2px] transition-colors ${
                                            key === todayKey ? 'bg-red-50/40' : 'bg-white'
                                        } hover:bg-gray-50`}
                                    >
                                        {entries.map(event => (
                                            <EventChip key={event.id} event={event} zone={zone} onClick={onOpen} />
                                        ))}

                                        {entries.length === 0 && (
                                            <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); onNewAt(key, hour); }}
                                                title={`Add an event at ${hourLabel(hour)}`}
                                                className="hidden w-full flex-1 items-center justify-center font-['Jersey_10'] text-[14px] leading-none text-gray-400 group-hover:flex hover:text-maroon"
                                            >
                                                +
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>

            <button type="button" onClick={onToggleHours} className={`${DESK_BUTTON_CLASS} self-start`}>
                {allHours ? 'Trim to busy hours' : 'Show all 24 hours'}
            </button>
        </div>
    );
}

function AgendaList({ dayLabel, entries, zone, userData, onOpen, onNew }) {
    return (
        <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-['Jersey_10'] text-[16px] uppercase tracking-[1px] text-maroon">{dayLabel}</span>
                <DeskNote>{entries.length === 1 ? '1 event' : `${entries.length} events`}</DeskNote>
            </div>

            {entries.length === 0 ? (
                <DeskEmpty>Nothing booked</DeskEmpty>
            ) : (
                <ul className="flex flex-col gap-2">
                    {entries.map(event => (
                        <li
                            key={event.id}
                            className="border border-gray-200 bg-white"
                            style={{ borderLeft: `4px solid ${typeColor(event.type)}` }}
                        >
                            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 px-2 pt-2">
                                <span className="font-['Jersey_10'] text-[16px] uppercase tracking-[1px] text-gray-800">
                                    {eventTimeLabel(event, zone)}
                                </span>
                                <span className="text-[11px] text-gray-400">
                                    {timeLabel(event.startTime, 'UTC')} UTC
                                </span>
                                <span className="ml-auto text-[11px] text-gray-500">{event.duration}</span>
                            </div>

                            <p className="break-words px-2 text-[15px] text-gray-900">{event.title}</p>

                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-2 pb-2 pt-1 text-[12px] text-gray-600">
                                <span>Host: {event.host}</span>
                                <span>Room: {event.roomName}</span>
                                <span>Owner: {event.roomOwner}</span>
                                <span>Prize: {event.prize}</span>
                            </div>

                            <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 px-2 py-[6px]">
                                <span
                                    className="rounded px-[6px] py-[1px] font-['Jersey_10'] text-[12px] uppercase leading-none tracking-[1px] text-white"
                                    style={{ backgroundColor: typeColor(event.type) }}
                                >
                                    {event.type}
                                </span>
                                {event.addedByName && (
                                    <span className="text-[11px] text-gray-400">set up by {event.addedByName}</span>
                                )}

                                <button
                                    type="button"
                                    onClick={() => onOpen(event)}
                                    className="ml-auto text-[12px] uppercase tracking-[1px] text-gray-600 underline hover:text-maroon"
                                >
                                    {canManageEvent(event, userData) ? 'Edit' : 'View'}
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            <button type="button" onClick={onNew} className={DESK_BUTTON_CLASS}>
                Add an event on this day
            </button>
        </div>
    );
}

function Schedule({ userData }) {
    const [zone, setZone] = useState(readStoredZone);
    const [zones] = useState(() => listTimeZones());

    const todayKey = useMemo(() => dayKey(new Date(), zone), [zone]);

    const [view, setView] = useState('month');
    const [anchor, setAnchor] = useState(todayKey);
    const [selected, setSelected] = useState(todayKey);
    const [allHours, setAllHours] = useState(false);

    const [events, setEvents] = useState([]);
    const [people, setPeople] = useState({ hosts: [], creators: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [reloadToken, setReloadToken] = useState(0);

    const [hostFilter, setHostFilter] = useState('');
    const [creatorFilter, setCreatorFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');

    const [wizard, setWizard] = useState(null);

    useEffect(() => { writeStoredZone(zone); }, [zone]);

    const range = useMemo(() => rangeForView(view, anchor), [view, anchor]);

    useEffect(() => {
        let active = true;

        const from = zonedToUtc(range.fromKey, '00:00', zone);
        const to = zonedToUtc(range.toKey, '00:00', zone);

        setLoading(true);

        getStaffEvents({
            from: from.toISOString(),
            to: to.toISOString(),
            host: hostFilter,
            addedBy: creatorFilter,
            type: typeFilter
        })
            .then(result => {
                if (!active) return;

                if (result?.success) {
                    setEvents(result.events || []);
                    setError('');
                } else {
                    setError(result?.message || 'Could not load the schedule.');
                }

                setLoading(false);
            })
            .catch(() => {
                if (!active) return;
                setError('Could not load the schedule.');
                setLoading(false);
            });

        return () => { active = false; };
    }, [range, zone, hostFilter, creatorFilter, typeFilter, reloadToken]);

    useEffect(() => {
        let active = true;

        getStaffEventPeople()
            .then(result => {
                if (active && result?.success) {
                    setPeople({ hosts: result.hosts || [], creators: result.creators || [] });
                }
            })
            .catch(() => {});

        return () => { active = false; };
    }, [reloadToken]);

    const byDay = useMemo(() => eventsByDay(events, zone), [events, zone]);

    const monthCells = useMemo(() => {
        const start = weekStartKey(`${anchor.slice(0, 7)}-01`);
        const cells = Array.from({ length: 42 }, (_, index) => addDaysToKey(start, index));
        const monthPrefix = anchor.slice(0, 7);

        while (cells.length > 35 && !cells.slice(35).some(key => key.slice(0, 7) === monthPrefix)) {
            cells.length = 35;
        }

        return cells;
    }, [anchor]);

    const days = useMemo(() => weekKeys(weekStartKey(anchor)), [anchor]);

    useEffect(() => {
        if (view === 'day') setSelected(anchor);
    }, [view, anchor]);

    const shift = (direction) => {
        const next = view === 'month'
            ? shiftMonthKey(anchor, direction)
            : addDaysToKey(anchor, direction * (view === 'week' ? 7 : 1));

        setAnchor(next);

        const bounds = rangeForView(view, next);
        if (selected < bounds.fromKey || selected >= bounds.toKey) {
            setSelected(view === 'month' ? `${next.slice(0, 7)}-01` : bounds.fromKey);
        }
    };

    const jumpToToday = () => {
        setAnchor(todayKey);
        setSelected(todayKey);
    };

    const selectDay = (key) => {
        setSelected(key);
        if (view === 'day') setAnchor(key);
    };

    const openEvent = (event) => {
        setSelected(dayKey(event.startTime, zone));
        setWizard({ event });
    };

    const openNew = (key, hour) => setWizard({
        event: null,
        dateKey: key || selected,
        startTime: hour === undefined ? undefined : `${pad(hour)}:00`
    });

    const handleDelete = async (event) => {
        if (!window.confirm(`Remove "${event.title}" from the schedule?`)) return;

        const result = await deleteStaffEvent(event.id);

        if (!result?.success) {
            toast.error(result?.message || 'Could not remove that event.');
            return;
        }

        toast.success('Event removed.');
        setWizard(null);
        setReloadToken(token => token + 1);
    };

    const handleSaved = () => {
        toast.success(wizard?.event ? 'Event updated.' : 'Event added.');
        setWizard(null);
        setReloadToken(token => token + 1);
    };

    if (!userData?.hasStaffRole) {
        return <Navigate to="/" replace />;
    }

    const rangeLabel = view === 'month'
        ? readableMonth(anchor)
        : view === 'week'
            ? readableRange(range.fromKey, range.toKey)
            : readableDayLabel(anchor);

    const selectedEntries = byDay.get(selected) || [];
    const isMonth = view === 'month';

    return (
        <EventsDesk>
            <DeskRibbon>
                <DeskTitle>Schedule</DeskTitle>

                <div className="flex items-center gap-1">
                    {VIEWS.map(entry => (
                        <button
                            key={entry.key}
                            type="button"
                            onClick={() => { setView(entry.key); setAnchor(selected); }}
                            className={`${DESK_BUTTON_CLASS} ${view === entry.key ? '!bg-gray-800 !text-white' : ''}`}
                        >
                            {entry.label}
                        </button>
                    ))}
                </div>

                <div className="flex items-center gap-1">
                    <button type="button" onClick={() => shift(-1)} className={DESK_BUTTON_CLASS}>&lt;</button>
                    <button type="button" onClick={jumpToToday} className={DESK_BUTTON_CLASS}>Today</button>
                    <button type="button" onClick={() => shift(1)} className={DESK_BUTTON_CLASS}>&gt;</button>
                </div>

                <span className="font-['Jersey_10'] text-[17px] uppercase tracking-[1px] text-gray-700">
                    {rangeLabel}
                </span>

                {loading && <DeskNote>Loading...</DeskNote>}

                <button
                    type="button"
                    onClick={() => openNew()}
                    className={`${DESK_PRIMARY_CLASS} ml-auto`}
                    style={DESK_PRIMARY_STYLE}
                >
                    New event
                </button>
            </DeskRibbon>

            <DeskPanel title="Showing" padding="p-3">
                <div className="flex flex-wrap items-end gap-3">
                    <label className="flex flex-col gap-1">
                        <span className="font-['Jersey_10'] text-[13px] uppercase tracking-[1px] text-gray-500">
                            Timezone
                        </span>
                        <select
                            value={zone}
                            onChange={(e) => { if (isValidZone(e.target.value)) setZone(e.target.value); }}
                            className="rounded border border-gray-300 px-2 py-1 text-[13px] text-gray-800"
                        >
                            {zones.map(entry => (
                                <option key={entry} value={entry}>
                                    {entry.replace(/_/g, ' ')} ({offsetLabel(entry)})
                                </option>
                            ))}
                        </select>
                    </label>

                    <button
                        type="button"
                        onClick={() => setZone('UTC')}
                        className={`${DESK_BUTTON_CLASS} ${zone === 'UTC' ? '!bg-gray-800 !text-white' : ''}`}
                    >
                        Use UTC
                    </button>

                    <label className="flex flex-col gap-1">
                        <span className="font-['Jersey_10'] text-[13px] uppercase tracking-[1px] text-gray-500">
                            Host
                        </span>
                        <select
                            value={hostFilter}
                            onChange={(e) => setHostFilter(e.target.value)}
                            className="rounded border border-gray-300 px-2 py-1 text-[13px] text-gray-800"
                        >
                            <option value="">Everyone</option>
                            {people.hosts.map(host => (
                                <option key={host.name} value={host.name}>
                                    {host.name} ({host.events})
                                </option>
                            ))}
                        </select>
                    </label>

                    <label className="flex flex-col gap-1">
                        <span className="font-['Jersey_10'] text-[13px] uppercase tracking-[1px] text-gray-500">
                            Set up by
                        </span>
                        <select
                            value={creatorFilter}
                            onChange={(e) => setCreatorFilter(e.target.value)}
                            className="rounded border border-gray-300 px-2 py-1 text-[13px] text-gray-800"
                        >
                            <option value="">Anyone</option>
                            {people.creators.map(creator => (
                                <option key={creator.userId} value={creator.userId}>
                                    {creator.name} ({creator.events})
                                </option>
                            ))}
                        </select>
                    </label>

                    <label className="flex flex-col gap-1">
                        <span className="font-['Jersey_10'] text-[13px] uppercase tracking-[1px] text-gray-500">
                            Type
                        </span>
                        <select
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value)}
                            className="rounded border border-gray-300 px-2 py-1 text-[13px] text-gray-800"
                        >
                            <option value="">All types</option>
                            {EVENT_TYPES.map(type => (
                                <option key={type.name} value={type.name}>{type.name}</option>
                            ))}
                        </select>
                    </label>

                    {(hostFilter || creatorFilter || typeFilter) && (
                        <button
                            type="button"
                            onClick={() => { setHostFilter(''); setCreatorFilter(''); setTypeFilter(''); }}
                            className={DESK_BUTTON_CLASS}
                        >
                            Clear filters
                        </button>
                    )}

                    <div className="ml-auto flex flex-wrap items-center gap-3">
                        {EVENT_TYPES.map(type => (
                            <span key={type.name} className="flex items-center gap-[6px] text-[12px] text-gray-600">
                                <span className="h-3 w-3 rounded-sm" style={{ backgroundColor: type.color }}></span>
                                {type.name}
                            </span>
                        ))}
                    </div>
                </div>

                <p className="mt-2 text-[12px] text-gray-500">
                    Times shown in {zone.replace(/_/g, ' ')} ({offsetLabel(zone)}). Everything is stored as UTC.
                </p>
            </DeskPanel>

            {error && (
                <p className="border border-red-300 bg-red-50 px-3 py-2 text-[13px] text-red-700">{error}</p>
            )}

            <div className={`grid grid-cols-1 gap-3 ${isMonth ? 'xl:grid-cols-3' : ''}`}>
                {view !== 'day' && (
                    <div className={isMonth ? 'xl:col-span-2' : ''}>
                        <DeskPanel title={isMonth ? readableMonth(anchor) : 'This week'}>
                            {isMonth ? (
                                <MonthGrid
                                    cells={monthCells}
                                    byDay={byDay}
                                    todayKey={todayKey}
                                    selected={selected}
                                    monthKey={anchor}
                                    zone={zone}
                                    onSelect={selectDay}
                                    onOpen={openEvent}
                                />
                            ) : (
                                <WeekGrid
                                    days={days}
                                    byDay={byDay}
                                    todayKey={todayKey}
                                    selected={selected}
                                    zone={zone}
                                    allHours={allHours}
                                    onToggleHours={() => setAllHours(prev => !prev)}
                                    onSelect={selectDay}
                                    onOpen={openEvent}
                                    onNewAt={(key, hour) => { setSelected(key); openNew(key, hour); }}
                                />
                            )}
                        </DeskPanel>
                    </div>
                )}

                <div className={isMonth ? 'xl:col-span-1' : ''}>
                    <DeskPanel title="On the day">
                        <AgendaList
                            dayLabel={readableDayLabel(selected)}
                            entries={selectedEntries}
                            zone={zone}
                            userData={userData}
                            onOpen={openEvent}
                            onNew={() => openNew(selected)}
                        />
                    </DeskPanel>
                </div>
            </div>

            {wizard && (
                <EventWizard
                    userData={userData}
                    zone={zone}
                    event={wizard.event}
                    dateKey={wizard.dateKey || selected}
                    startTime={wizard.startTime}
                    onClose={() => setWizard(null)}
                    onSaved={handleSaved}
                    onDelete={wizard.event && canManageEvent(wizard.event, userData) ? handleDelete : null}
                />
            )}
        </EventsDesk>
    );
}

export default Schedule;
